import type { HTMLAttributes, ReactNode } from "react";

import { useAuth } from "../../../providers/AuthProvider";
import { SiteFooter } from "../../../components/SiteFooter";

type PageShellWidth = "default" | "narrow" | "wide";
type PageShellTone = "surface" | "plain";
type PageShellPadding = "default" | "none";

interface PageShellProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  width?: PageShellWidth;
  tone?: PageShellTone;
  padding?: PageShellPadding;
  showFooter?: boolean;
  footerVariant?: "standalone" | "embedded";
  showFooterSignout?: boolean;
}

export function PageShell({
  children,
  className,
  width = "default",
  tone = "surface",
  padding = "default",
  showFooter,
  footerVariant = "embedded",
  showFooterSignout = true,
  ...rest
}: PageShellProps) {
  const { accessToken } = useAuth();
  const resolvedShowFooter = showFooter ?? !accessToken;
  const widthClasses: Record<PageShellWidth, string> = {
    default: "max-w-5xl",
    wide: "max-w-6xl",
    narrow: "max-w-3xl",
  };
  const wrapperBase =
    "min-h-screen w-full px-safe px-4 pb-safe pt-safe overflow-x-hidden sm:px-6 lg:py-10";
  const wrapperTone =
    tone === "surface"
      ? "bg-slate-100 dark:bg-slate-950"
      : "bg-transparent dark:bg-slate-950";
  const contentBase =
    "w-full rounded-none shadow-none ring-0 sm:rounded-3xl sm:shadow-2xl sm:ring-1 ring-slate-100 dark:ring-slate-800 flex min-h-[60vh] flex-col gap-8";
  const contentTone =
    tone === "surface"
      ? "bg-white text-slate-900 dark:bg-slate-900 dark:text-slate-100"
      : "bg-transparent shadow-none ring-0 text-slate-900 dark:text-slate-100";
  const contentPadding =
    padding === "none"
      ? "p-0"
      : "px-4 py-6 sm:px-8 sm:py-10";
  const outerClasses = [wrapperBase, wrapperTone]
    .filter(Boolean)
    .join(" ");
  const innerClasses = [
    "mx-auto flex w-full flex-col gap-6 lg:py-6",
    widthClasses[width],
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const contentClasses = [
    contentBase,
    contentTone,
    contentPadding,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={outerClasses} {...rest}>
      <div className={innerClasses}>
        <div className={contentClasses}>
          {children}
          {resolvedShowFooter ? (
            <div className="mt-auto sm:mt-4">
              <div className="sticky bottom-3 z-20 pb-safe sm:static sm:pb-0">
                <SiteFooter
                  className="mt-4"
                  variant={footerVariant}
                  showSignout={showFooterSignout}
                />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
